import { skillTableResponseSchema, listSkillsDataTableParamsSchema, skillFilterOptionsQuerySchema, skillFilterOptionsResponseSchema, skillAllowVersionBodySchema, skillAllowVersionResponseSchema, skillRiskDetailSchema, skillRiskQuerySchema, } from '../contracts/skills';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
/**
 * Fetch skills in DataTable format (pagination, sorting, search, list state).
 *
 * Organization scope only — there is no account-scoped skills route, so the
 * Inventory → Skills tab is shown only inside an organization.
 *
 * Responds 404 while the `skills_inventory` flag is off for the org, which the
 * table surfaces as an error state rather than an empty list.
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - DataTable query parameters
 * @param options - Optional AbortSignal for request cancellation
 */
export async function listSkillsDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listSkillsDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/skills/datatable`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(skillTableResponseSchema, response);
}
/**
 * Distinct values for one Skills column-filter dropdown.
 *
 * Paginated and server-searched: an org with thousands of skills must not
 * ship every value to render a dropdown, so the caller pages with
 * `offset`/`limit` and narrows with `search`.
 *
 * Read `optionsWithMetadata` for display — for `projects` and `users` the
 * `options` array holds slugs and ids, which are the filter's keys and not
 * meant to be shown.
 */
export async function listSkillsFilterOptions(client, organizationId, query, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    // The schema caps `search` at 200 so the ILIKE stays bounded. A longer
    // dropdown paste used to throw here, before the request. Keep the bound
    // and search the leading 200 characters.
    const validatedQuery = parseSchema(skillFilterOptionsQuerySchema, {
        ...query,
        ...(typeof query.search === 'string'
            ? { search: query.search.slice(0, 200) }
            : {}),
    });
    const response = await client.get(`/organizations/${validatedId}/skills/filter-options`, { params: validatedQuery, signal: options?.signal });
    return parseSchema(skillFilterOptionsResponseSchema, response);
}
/**
 * Allow one listed version of a skill (KIR-5174). Removes the rule naming
 * that version and splits any every-version rule so the skill's other
 * versions stay marked. See `skillAllowVersionBodySchema`.
 */
export async function allowSkillVersion(client, organizationId, body) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(skillAllowVersionBodySchema, body);
    const response = await client.post(`/organizations/${validatedId}/skills/allow-version`, validatedBody);
    return parseSchema(skillAllowVersionResponseSchema, response);
}
/**
 * The AgentMesh risk of one listed skill (KIR-5099), for the expanded row's
 * risk panel: its verdict and every distinct copy of its files with its own
 * verdict, factors and AgentMesh link. Reads Kirin's cache only.
 *
 * Responds 404 while the `skills_inventory` flag is off for the org.
 */
export async function getSkillRisk(client, organizationId, query, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedQuery = parseSchema(skillRiskQuerySchema, query);
    const response = await client.get(`/organizations/${validatedId}/skills/risk`, { params: validatedQuery, signal: options?.signal });
    return parseSchema(skillRiskDetailSchema, response);
}
//# sourceMappingURL=skills.js.map